<template>
  <div>
    <h1 id="title">Download Music</h1>
    <p id="download-instructions">
      Each download includes <b>instrumentals</b> and <b>full songs</b> in .mp3
      and .wav formats.
    </p>
    <div id="downloads-grid">
      <div v-for="album in downloads" :key="album.description" class="album">
        <div class="album-card" @click="openAlbum(album)">
          <div class="album-art-container">
            <img :src="album.albumArtUrl" class="album-art" />
            <div class="play-overlay">
              <FontAwesomeIcon icon="fa-solid fa-play" />
            </div>
          </div>
          <div class="album-label">{{ album.description }}</div>
        </div>
      </div>
    </div>

    <MusicPlayerCard
      :album="selectedAlbum"
      @close="selectedAlbum = null"
      @download="registerMusicDownload"
    />
  </div>
</template>

<script>
import cwhos from "../assets/cwhos.jpg";
import ep4 from "../assets/ep4.png";
import ep3 from "../assets/ep3.jpg";
import ep1 from "../assets/ep1.jpg";
import ep2 from "../assets/ep2.jpg";
import chiptunes from "../assets/8bit.jpg";
import acoustic from "../assets/acoustic.jpg";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import MusicPlayerCard from "./MusicPlayerCard.vue";

library.add(faPlay);

const MEDIA_BASE_URL = "https://storage.googleapis.com/gk-media/audio";

export default {
  name: "MusicDownloads",
  components: {
    FontAwesomeIcon,
    MusicPlayerCard,
  },
  data() {
    return {
      selectedAlbum: null,
      downloads: [
        {
          description: "Can We Hang Out Sometime?",
          albumArtUrl: cwhos,
          mediaSlug: "cwhos",
          tracks: [],
        },
        {
          description: "Good Kid 4 (EP)",
          albumArtUrl: ep4,
          mediaSlug: "ep4",
          tracks: [],
        },
        {
          description: "Good Kid 3 (EP)",
          albumArtUrl: ep3,
          mediaSlug: "ep3",
          tracks: [],
        },
        {
          description: "Good Kid 2 (EP)",
          albumArtUrl: ep2,
          mediaSlug: "ep2",
          tracks: [],
        },
        {
          description: "Good Kid (EP)",
          albumArtUrl: ep1,
          mediaSlug: "ep1",
          tracks: [],
        },
        {
          description: "8 Bit Tracks (OST)",
          albumArtUrl: chiptunes,
          mediaSlug: "8bit",
          tracks: [],
        },
        {
          description: "Acoustic Kid",
          albumArtUrl: acoustic,
          mediaSlug: "acoustic-kid",
          tracks: [],
        },
      ],
    };
  },

  methods: {
    async openAlbum(album) {
      if (album.mediaSlug && album.tracks.length === 0) {
        try {
          const res = await fetch(
            `${MEDIA_BASE_URL}/${album.mediaSlug}/manifest.json?_=${Date.now()}`
          );
          const manifest = await res.json();
          const baseUrl = `${MEDIA_BASE_URL}/${album.mediaSlug}`;

          if (manifest.album_slug) {
            album.url = `${baseUrl}/${manifest.album_slug}.zip`;
          }

          album.tracks = manifest.tracks.map((t) => {
            const track = { name: t.name };
            if (t.available === false) {
              track.unavailable = true;
              return track;
            }
            if (manifest.folders.full) {
              const streamFmt = manifest.folders.full.formats.includes("mp3")
                ? "mp3"
                : manifest.folders.full.formats[0];
              track.src = `${baseUrl}/full/${t.slug}.${streamFmt}`;
              if (manifest.folders.full.formats.includes("wav")) {
                track.downloadSrc = `${baseUrl}/full/${t.slug}.wav`;
              }
            }
            if (manifest.folders.instrumental) {
              const streamFmt = manifest.folders.instrumental.formats.includes("mp3")
                ? "mp3"
                : manifest.folders.instrumental.formats[0];
              track.instrumentalSrc = `${baseUrl}/instrumental/${t.slug}.${streamFmt}`;
              if (manifest.folders.instrumental.formats.includes("wav")) {
                track.instrumentalDownloadSrc = `${baseUrl}/instrumental/${t.slug}.wav`;
              }
            }
            return track;
          });
        } catch (e) {
          console.error("Failed to load album manifest:", e);
        }
      }
      this.selectedAlbum = album;
    },

    registerMusicDownload(album) {
      this.$gtag.event("album_download", { album });
    },
  },
};
</script>

<style scoped>
#downloads-grid {
  display: grid;
  grid-template-columns:
    minmax(120px, max-content)
    repeat(auto-fill, 120px);
  grid-gap: 40px 24px;
  justify-content: center;
  justify-items: center;
}

.album-art {
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  width: 120px;
  display: block;
}

.album-card {
  cursor: pointer;
  text-align: center;
  width: 140px;
}

.album-art-container {
  position: relative;
  display: inline-block;
}

.play-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: white;
  font-size: 2em;
  pointer-events: none;
}

.album-card:hover .play-overlay {
  opacity: 1;
}

.album-label {
  font-weight: 500;
  color: #3d3530;
  font-size: 0.88em;
  margin-top: 6px;
  transition: color 0.2s ease;
}

.album-card:hover .album-label {
  color: #6b5290;
}

.album {
  text-align: center;
}

@media screen and (min-width: 60rem) {
  .album-art {
    width: 200px;
    opacity: 90%;
  }

  .album-card:hover .album-art {
    opacity: 100%;
  }

  #downloads-grid {
    min-width: 700px;
    grid-gap: 50px 50px;
    grid-template-columns:
      minmax(200px, max-content)
      repeat(auto-fill, 200px);
  }

  .album-card {
    width: 200px;
  }

  #download-instructions {
    padding-bottom: 20px;
  }

  #title {
    margin-bottom: 5px;
  }
}
</style>
